// Shared path snapshot + restore helpers for workspace local and Tandem disk tools.
// Extracted from BasicWorkspaceAIToolSet so agent-write checkpoints work the same way.

export function normalizeEntryPath(path) {
  if (!path || typeof path !== "string") {
    return null;
  }
  return path.startsWith("/") ? path : `/${path}`;
}

export async function captureDirectoryTree(rootPath, readTool, listTool) {
  const tree = {
    root: rootPath,
    directories: [],
    files: [],
  };

  const processDirectory = async (dirPath, relativePath) => {
    try {
      const listResult = await listTool.listDirectory(dirPath, false, false, false);
      if (!listResult?.success || !listResult?.data?.entries) {
        return;
      }

      for (const entry of listResult.data.entries) {
        const entryPath = dirPath === "/" ? `/${entry.name}` : `${dirPath}/${entry.name}`;
        const entryRelativePath =
          relativePath === "/" ? `/${entry.name}` : `${relativePath}/${entry.name}`;

        if (entry.type === "directory") {
          tree.directories.push({
            path: entryRelativePath,
            name: entry.name,
          });
          await processDirectory(entryPath, entryRelativePath);
        } else if (entry.type === "file") {
          const readResult = await readTool.readFile(entryPath, "base64");
          if (readResult?.success) {
            tree.files.push({
              path: entryRelativePath,
              name: entry.name,
              content: readResult.data.content,
              encoding: "base64",
            });
          }
        }
      }
    } catch (error) {
      console.warn(`Failed to process directory ${dirPath}:`, error);
    }
  };

  await processDirectory(rootPath, "/");
  return tree;
}

export async function captureProjectPathState({ path, readTool, listTool }) {
  const normalizedPath = normalizeEntryPath(path);
  if (!normalizedPath || !readTool) {
    return null;
  }

  const exists = await readTool.checkExists(normalizedPath);
  if (!exists) {
    return {
      path: normalizedPath,
      exists: false,
      kind: "missing",
    };
  }

  if (listTool) {
    const listResult = await listTool.listDirectory(normalizedPath, true, false, false);
    if (listResult?.success && Array.isArray(listResult?.data?.entries)) {
      return {
        path: normalizedPath,
        exists: true,
        kind: "folder",
        tree: await captureDirectoryTree(normalizedPath, readTool, listTool),
      };
    }
  }

  const readResult = await readTool.readFile(normalizedPath, "base64");
  return {
    path: normalizedPath,
    exists: true,
    kind: "file",
    content: readResult?.success ? readResult?.data?.content ?? null : null,
    encoding: "base64",
  };
}

export async function restoreFolderTree(tree, createFolderTool, writeFileTool) {
  if (!tree || !tree.directories || !tree.files) {
    return { success: false, error: "Invalid folder tree data" };
  }

  try {
    const sortedDirs = tree.directories.sort((a, b) => {
      const aDepth = a.path.split("/").filter(Boolean).length;
      const bDepth = b.path.split("/").filter(Boolean).length;
      return aDepth - bDepth;
    });

    for (const dir of sortedDirs) {
      const fullPath = tree.root === "/" ? dir.path : `${tree.root}${dir.path}`;
      const createResult = await createFolderTool.createFolder(fullPath, true, true);
      if (!createResult.success) {
        console.warn(`Failed to create directory ${fullPath}:`, createResult.message);
      }
    }

    for (const file of tree.files) {
      const fullPath = tree.root === "/" ? file.path : `${tree.root}${file.path}`;
      const writeResult = await writeFileTool.writeFile(
        fullPath,
        file.content,
        true,
        file.encoding || "base64",
        true
      );
      if (!writeResult.success) {
        console.warn(`Failed to restore file ${fullPath}:`, writeResult.message);
      }
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export async function restoreProjectPathState(
  state,
  { readTool, deleteTool, writeTool, createFolderTool }
) {
  const normalizedPath = normalizeEntryPath(state?.path);
  if (!normalizedPath) {
    return { success: false, error: "Invalid path state" };
  }

  const currentExists = readTool ? await readTool.checkExists(normalizedPath) : false;
  if (!state?.exists || state?.kind === "missing") {
    if (currentExists) {
      return deleteTool.deleteFile(normalizedPath, true);
    }
    return { success: true };
  }

  if (state.kind === "file") {
    if (currentExists) {
      const deleteResult = await deleteTool.deleteFile(normalizedPath, true);
      if (!deleteResult?.success) {
        return deleteResult;
      }
    }
    return writeTool.writeFile(
      normalizedPath,
      state.content || "",
      true,
      state.encoding || "base64",
      true
    );
  }

  if (state.kind === "folder") {
    if (currentExists) {
      const deleteResult = await deleteTool.deleteFile(normalizedPath, true);
      if (!deleteResult?.success) {
        return deleteResult;
      }
    }

    const createRootResult = await createFolderTool.createFolder(normalizedPath, true, true);
    if (!createRootResult?.success) {
      return createRootResult;
    }

    if (state.tree) {
      return restoreFolderTree(state.tree, createFolderTool, writeTool);
    }

    return { success: true };
  }

  return { success: false, error: `Unsupported path state kind: ${state?.kind || "unknown"}` };
}
